import { useState, useEffect, useRef } from "react";
import { useOrg } from "../../contexts/OrgContext";
import { apiClient } from "../../utils/apiClient/index.ts";
import Label from "../../components/common/form/Label";
import Input from "../../components/common/form/input/InputField";
import Button from "../../components/common/ui/button/Button";
import { usePageMeta } from "../../hooks/usePageMeta";
import { Upload, Trash2 } from "lucide-react";
import type { Organization } from "../../types";

const ALLOWED_FILE_TYPES = ["image/png", "image/jpeg", "image/webp"];
const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB
const LOGO_ELIGIBLE_PLANS = ["SMB", "MID"];

function getInitials(name: string): string {
  return name
    .split(" ")
    .map((word) => word[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

export default function OrganizationSettingsPage() {
  usePageMeta({ title: "Organization Settings", description: "Manage organization settings" });

  const { orgId } = useOrg();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Form state
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  // Load organization data
  useEffect(() => {
    const loadOrganization = async () => {
      if (!orgId) return;

      try {
        setLoading(true);
        const org = await apiClient.getOrganization(orgId);
        setOrganization(org);
        setName(org.name);
        setDescription(org.description || "");
      } catch (err: any) {
        console.error("Error loading organization:", err);
        setError(err.message || "Failed to load organization");
      } finally {
        setLoading(false);
      }
    };

    loadOrganization();
  }, [orgId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!orgId || !organization) return;

    setError(null);
    setSuccess(false);
    setSaving(true);

    try {
      const updateData: { name?: string; description?: string } = {};

      if (name !== organization.name) {
        updateData.name = name;
      }

      if (description !== organization.description) {
        updateData.description = description;
      }

      if (Object.keys(updateData).length > 0) {
        const updated = await apiClient.updateOrganization(orgId, updateData);
        setOrganization(updated);
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
      } else {
        setError("No changes to save");
      }
    } catch (err: any) {
      console.error("Error updating organization:", err);
      setError(err.message || "Failed to update organization");
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !orgId) return;

    // Validate file type
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      setError("Invalid file type. Please upload a PNG, JPG, or WebP image.");
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setError("File too large. Maximum size is 2MB.");
      return;
    }

    setError(null);
    setUploadingLogo(true);

    try {
      const updated = await apiClient.uploadOrganizationLogo(orgId, file);
      setOrganization(updated);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      console.error("Error uploading logo:", err);
      setError(err.message || "Failed to upload logo");
    } finally {
      setUploadingLogo(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleDeleteLogo = async () => {
    if (!orgId || !organization?.logoUrl) return;

    setError(null);
    setUploadingLogo(true);

    try {
      const updated = await apiClient.deleteOrganizationLogo(orgId);
      setOrganization(updated);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      console.error("Error deleting logo:", err);
      setError(err.message || "Failed to delete logo");
    } finally {
      setUploadingLogo(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-gray-600 dark:text-gray-400">Loading...</p>
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-red-600 dark:text-red-400">Organization not found</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Organization Settings
        </h1>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          Manage your organization's profile and branding
        </p>
      </div>

      <div className="space-y-6">
        {error && (
          <div className="rounded-lg bg-red-50 p-4 dark:bg-red-900/20">
            <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
          </div>
        )}

        {success && (
          <div className="rounded-lg bg-green-50 p-4 dark:bg-green-900/20">
            <p className="text-sm text-green-800 dark:text-green-200">
              Settings updated successfully!
            </p>
          </div>
        )}

        {/* Logo Section - Only available for SMB and MID plans */}
        {LOGO_ELIGIBLE_PLANS.includes(organization.planId) && (
          <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-dark">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              Organization Logo
            </h2>

            <div className="flex items-start gap-6">
              {/* Logo Preview */}
              <div className="flex-shrink-0">
                {organization.logoUrl ? (
                  <img
                    src={organization.logoUrl}
                    alt={organization.name}
                    className="h-20 w-auto max-w-[200px] rounded-lg border border-gray-200 object-contain dark:border-gray-700"
                  />
                ) : (
                  <div className="flex h-20 w-20 items-center justify-center rounded-lg bg-blue-600">
                    <span className="text-2xl font-bold text-white">
                      {getInitials(organization.name)}
                    </span>
                  </div>
                )}
              </div>

              {/* Upload Controls */}
              <div className="flex-1">
                <p className="mb-3 text-sm text-gray-600 dark:text-gray-400">
                  Upload a logo to represent your organization in the client portal.
                  Recommended size: 400x100px (wide) or 200x200px (square). Max 2MB.
                  Supported formats: PNG, JPG, WebP.
                </p>

                <div className="flex gap-3">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".png,.jpg,.jpeg,.webp"
                    onChange={handleLogoUpload}
                    className="hidden"
                    id="logo-upload"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingLogo}
                  >
                    <Upload className="mr-2 h-4 w-4" />
                    {uploadingLogo ? "Uploading..." : "Upload Logo"}
                  </Button>

                  {organization.logoUrl && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleDeleteLogo}
                      disabled={uploadingLogo}
                      className="text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Remove Logo
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Organization Details Form */}
        <form onSubmit={handleSubmit}>
          <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-dark">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              Organization Details
            </h2>

            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Organization Name</Label>
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter organization name"
                  className="mt-1"
                  required
                />
              </div>

              <div>
                <Label htmlFor="description">Description (Optional)</Label>
                <textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Brief description of your organization"
                  className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                  rows={3}
                />
              </div>
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setName(organization.name);
                setDescription(organization.description || "");
                setError(null);
                setSuccess(false);
              }}
            >
              Reset
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
